import { supabase } from './supabase';
import { getOrCreateSessionId } from '../utils/session';
import { APP_VERSION, PROMPT_VERSION } from '../utils/appVersion';
import { moduloCanonico } from './modulos';
import { plataformaActual, aparatoActual } from './platform';
import { fijarMundoActual, mundoActual } from './mundoActual';

// ── Contexto de tracking ──────────────────────────────────────────────────────
// Inicializado por App.jsx una vez que session + profile están disponibles.
// Todos los módulos usan este estado global — no es necesario pasar userId
// a cada componente.

let _userId   = null;
let _cohortId = null;

export function setTrackingContext({ userId, cohortId }) {
  _userId   = userId   || null;
  _cohortId = cohortId || null;
}

// Mundo visitado (tres mundos, docs/plan-tres-mundos.md). Mientras la persona
// está fuera de su casa, TODO evento lleva `mundo` en el payload, venga de App
// o del propio módulo: así lo que se usa de visita no se confunde con el uso
// por capa en casa (aperturas de módulo, uso del lanzamiento, eventos de la
// capa TENS). En casa es null y el payload no cambia. Catálogo cerrado
// (estudiante | enfermero | tens), lo fija App.
export function setMundoTracking(mundo) {
  fijarMundoActual(mundo);
}

// ── Cola offline simple ───────────────────────────────────────────────────────
// Si el insert falla por red, guarda en memoria y reintenta al volver online.
// Máximo 50 eventos en cola para no consumir memoria excesiva.

const QUEUE_MAX = 50;
let _offlineQueue = [];
let _flushScheduled = false;

function scheduleFlush() {
  if (_flushScheduled) return;
  _flushScheduled = true;
  window.addEventListener('online', flushQueue, { once: true });
}

async function flushQueue() {
  _flushScheduled = false;
  const batch = _offlineQueue.splice(0);
  if (!batch.length) return;
  const { error } = await supabase.from('eventos_v2').insert(batch);
  if (error) {
    console.warn('[eventTracking] flush failed:', error.message);
    _offlineQueue = [...batch.slice(0, QUEUE_MAX - _offlineQueue.length), ..._offlineQueue];
  }
}

// Plataforma de esta sesión, resuelta UNA vez por carga: no cambia a mitad de
// sesión y leer matchMedia en cada evento sería trabajo regalado. Ver
// lib/platform.js para el porqué del catálogo y del orden de detección.
const _plataforma = plataformaActual();
// El aparato (iphone/ipad/android/computador/otro) se resuelve igual, una vez
// por carga. Ver lib/platform.js (`clasificarAparato`).
const _aparato = aparatoActual();

// ── trackEvent ────────────────────────────────────────────────────────────────
//
// Emite una fila a eventos_v2. Fire-and-forget — nunca lanza error al caller.
//
// evento:     nombre del evento (ej. 'modulo_abierto', 'paso_completado')
// modulo:     id del módulo (ej. 'pae_rapido', 'revisa', 'calendario')
// paso:       string opcional (ej. 'valoracion', 'diagnosticos')
// duracion_ms: entero ms opcional (solo en paso_completado)
// payload:    jsonb adicional
//
export function trackEvent(evento, { modulo, paso = null, duracion_ms = null, payload = {} } = {}) {
  if (!_userId) return; // table requires user_id NOT NULL
  const mundo = mundoActual();
  if (mundo) payload = { ...(payload || {}), mundo };

  const row = {
    user_id:        _userId,
    session_id:     getOrCreateSessionId(),
    cohort_id:      _cohortId,
    // Normalización canónica central: garantiza que ningún call-site pueda dejar
    // un nombre de módulo inconsistente en eventos_v2 (fuente única en modulos.js).
    modulo:         moduloCanonico(modulo),
    evento,
    paso:           paso || null,
    duracion_ms:    duracion_ms ?? null,
    payload:        payload && Object.keys(payload).length > 0 ? payload : {},
    app_version:    APP_VERSION,
    prompt_version: PROMPT_VERSION,
    // Columna que existía en eventos_v2 desde mayo sin que nadie la escribiera
    // (79.561 eventos, todos NULL). Es contexto del cliente, igual que
    // app_version, así que va como columna y no dentro del payload: se consulta
    // sin abrir el jsonb y sirve para TODOS los eventos, no solo los de Benito.
    plataforma:     _plataforma,
    // En qué aparato se usa MiPAE (2026-10-12): la pregunta "iPhone, Android
    // o computador" que `plataforma` no contesta. Catálogo cerrado.
    aparato:        _aparato,
  };

  if (!navigator.onLine) {
    if (_offlineQueue.length < QUEUE_MAX) _offlineQueue.push(row);
    scheduleFlush();
    return;
  }

  supabase.from('eventos_v2').insert(row).then(({ error }) => {
    if (error) {
      console.warn('[eventTracking] insert failed:', error.message);
      if (_offlineQueue.length < QUEUE_MAX) _offlineQueue.push(row);
      scheduleFlush();
    }
  });
}
